/**
 * Landing Page Component
 * Hero section + tool cards grid + "Why Kaalagyana" section + bottom CTA
 */

import React, { lazy, Suspense } from 'react';
import {
  Globe, Calendar, CalendarDays, Download, Sparkles, Star,
  BookOpen, GraduationCap, ChevronRight, Shield, Languages, Compass
} from 'lucide-react';
import MeditationIcon from '../icons/MeditationIcon';

const PlanetaryAlignmentCapture = lazy(() => import('../PlanetaryAlignmentCapture/PlanetaryAlignmentCapture'));

type ViewType =
  | 'daily-panchanga'
  | 'monthly-panchanga'
  | 'calendar-download'
  | 'mahabharata-dating'
  | 'ramayana-dating'
  | 'buddha-dating'
  | 'planetary-positions'
  | 'planetary-alignment'
  | 'full-chart'
  | 'muhurtha'
  | 'birth-chart'
  | 'learnings'
  | 'dhyana'
  | 'landing';

interface LandingProps {
  onNavigate: (view: ViewType) => void;
}

interface ToolCard {
  id: ViewType;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
}

const toolCards: ToolCard[] = [
  {
    id: 'daily-panchanga',
    title: 'Dina Pañcāṅga',
    subtitle: 'Daily Panchanga',
    description: 'Tithi, Nakṣatra, Yoga, Karaṇa, Vara - with festivals, Tārā Balam, and Homa recommendations. Any date, any location.',
    icon: <Calendar className="h-7 w-7" />,
  },
  {
    id: 'monthly-panchanga',
    title: 'Māsa Pañcāṅga',
    subtitle: 'Monthly Panchanga',
    description: 'All five elements for every day of the month. Includes Rāhu Kāla, Durmuhūrta, Amṛta Kāla, and festivals. Print-ready.',
    icon: <CalendarDays className="h-7 w-7" />,
  },
  {
    id: 'calendar-download',
    title: 'Vārṣika Pañcāṅga',
    subtitle: 'Download Calendar',
    description: 'A printable yearly calendar with fortnightly panchanga tables, kala vishesha, planetary transits, and festivals. Any city, 5 languages.',
    icon: <Download className="h-7 w-7" />,
  },
  {
    id: 'dhyana',
    title: 'Dhyāna',
    subtitle: 'Nāḍī Śodhana',
    description: 'Alternate-nostril breathing as the haṭha texts teach it, under a quiet dawn sky. Counted, unhurried, in 5 languages.',
    icon: <MeditationIcon className="h-7 w-7" />,
  },
  {
    id: 'planetary-alignment',
    title: 'Kāla Chakra',
    subtitle: 'Planetary Wheel',
    description: 'See where all nine grahas are right now in the sidereal zodiac. Live positions with Nakṣatra divisions.',
    icon: <Globe className="h-7 w-7" />,
  },
  {
    id: 'birth-chart',
    title: 'Janma Kuṇḍalī',
    subtitle: 'Birth Chart',
    description: 'Your Rāśi chart and all 16 Parāśara varga charts in South Indian format. Full Viṃśottarī Daśā tree.',
    icon: <Sparkles className="h-7 w-7" />,
  },
  {
    id: 'muhurtha',
    title: 'Śubha Kāla',
    subtitle: 'Auspicious Windows',
    description: 'When should you travel, start a job, or move? Scored across 9 Jyotiṣa dimensions, personalized to your birth chart.',
    icon: <Star className="h-7 w-7" />,
  },
  {
    id: 'mahabharata-dating',
    title: 'Itihāsa Kālanirṇaya',
    subtitle: 'Historical Datings',
    description: 'Exploring the dates of the Mahābhārata, Rāmāyaṇa, and Buddha by matching planetary positions described in the texts.',
    icon: <BookOpen className="h-7 w-7" />,
  },
  {
    id: 'learnings',
    title: 'Jñāna Kośa',
    subtitle: 'Knowledge Base',
    description: 'How does Ayanāṃśa work? What is Amṛta Kāla? Articles explaining the why and how behind every computation.',
    icon: <GraduationCap className="h-7 w-7" />,
  },
];

interface WhyCard {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const whyCards: WhyCard[] = [
  {
    icon: <Shield className="h-6 w-6" />,
    title: 'Astronomical Accuracy',
    description: 'All computations use the Swiss Ephemeris and are verified against the Indian Astronomical Ephemeris.',
  },
  {
    icon: <Compass className="h-6 w-6" />,
    title: 'Rooted in Tradition',
    description: 'Lahiri Ayanāṃśa, Sūrya Siddhānta conventions, Muhūrta Chintāmaṇi tables - not shortcuts or approximations.',
  },
  {
    icon: <Languages className="h-6 w-6" />,
    title: 'Built for Everyone',
    description: 'Available in 5 languages with proper script rendering: English, Sanskrit, Hindi, Telugu, Kannada.',
  },
];

const Landing: React.FC<LandingProps> = ({ onNavigate }) => {
  return (
    <div className="space-y-16 pb-12">
      {/* Hero Section - redesigned top portion (warm token look).
          Only the hero uses `.kg-warm`; the Explore/Why/CTA sections below are unchanged. */}
      <section className="kg-warm pt-6 md:pt-10">
        <div className="grid grid-cols-1 md:grid-cols-[1.05fr_0.95fr] items-center gap-10 md:gap-16">
          {/* Left: copy + CTAs */}
          <div className="text-center md:text-left">
            {/* Ephemeris credibility badge */}
            <div
              className="mb-6 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold tracking-[0.04em]"
              style={{
                background: 'var(--kg-badge-bg)',
                border: '1px solid var(--kg-badge-border)',
                color: 'var(--kg-faint)',
              }}
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: 'var(--kg-green)', boxShadow: '0 0 0 3px rgba(30,158,106,0.18)' }}
              />
              SWISS EPHEMERIS · LAHIRI AYANĀṂŚA
            </div>

            <h2
              className="font-fraunces mb-4 font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(3rem,7vw,4.75rem)]"
              style={{ color: 'var(--kg-ink)' }}
            >
              Kālagñāna
            </h2>
            <p
              className="font-fraunces mb-5 leading-tight text-[clamp(1.25rem,2.5vw,1.625rem)]"
              style={{ color: 'var(--kg-sub2)' }}
            >
              Time, computed the way the Ṛṣis intended.
            </p>
            <p
              className="mx-auto mb-9 max-w-[440px] text-[17px] leading-[1.55] md:mx-0"
              style={{ color: 'var(--kg-sub)' }}
            >
              Traditional Pañcāṅga, planetary positions, muhūrta windows, and birth charts - for any
              date, any place on Earth.
            </p>

            {/* One solid primary action; the rest are ghost buttons. */}
            <div className="flex flex-wrap items-center justify-center gap-4 md:justify-start">
              <button
                onClick={() => onNavigate('daily-panchanga')}
                className="kg-accent-btn inline-flex items-center gap-2.5 rounded-[11px] px-[26px] py-[15px] text-[15.5px] font-semibold"
              >
                Today's Pañcāṅga <span className="text-[17px]">→</span>
              </button>
              <button
                onClick={() => onNavigate('calendar-download')}
                className="kg-ghost inline-flex items-center gap-2 px-1.5 py-[15px] text-[15px] font-semibold transition-colors"
              >
                <Download className="h-4 w-4" /> Yearly Calendar
              </button>
              <button
                onClick={() => onNavigate('planetary-alignment')}
                className="kg-ghost inline-flex items-center gap-2 px-1.5 py-[15px] text-[15px] font-semibold transition-colors"
              >
                Open Kāla Chakra
              </button>
            </div>
          </div>

          {/* Right: navy chart card with the live Kāla Chakra wheel */}
          <div
            className="relative mx-auto w-full max-w-[420px]"
            role="complementary"
            aria-label="Live planetary positions wheel"
          >
            <div
              className="relative aspect-square overflow-hidden rounded-[22px]"
              style={{
                background: 'radial-gradient(circle at 50% 45%, #1a1636 0%, #0d0b1f 70%)',
                border: '1px solid #221d40',
                boxShadow: '0 30px 60px -30px rgba(20,16,50,0.5)',
              }}
            >
              <Suspense
                fallback={
                  <div className="flex h-full w-full items-center justify-center">
                    <Globe className="h-12 w-12 animate-pulse text-white/20" />
                  </div>
                }
              >
                <PlanetaryAlignmentCapture
                  mini
                  showPanels={false}
                  onClick={() => onNavigate('planetary-alignment')}
                />
              </Suspense>
              <div
                className="pointer-events-none absolute inset-x-0 bottom-4 text-center text-[12px] tracking-[0.02em]"
                style={{ color: '#9d94c4' }}
              >
                Live graha positions - click to explore
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tool Cards Grid */}
      <section className="space-y-6">
        <h3 className="text-center text-lg font-semibold text-muted-foreground uppercase tracking-wider">
          Explore
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {toolCards.map((card) => (
            <button
              key={card.id}
              onClick={() => onNavigate(card.id)}
              className={`group relative text-left p-5 rounded-xl border border-border/60 bg-card hover:border-brand/40 dark:hover:border-brand-dark/40 hover:shadow-lg hover:shadow-black/5 hover:-translate-y-0.5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
            >
              <div className="flex items-center gap-3 mb-3 min-h-[3rem]">
                <div className="text-muted-foreground group-hover:text-brand dark:group-hover:text-brand-dark transition-colors shrink-0">
                  {card.icon}
                </div>
                <div className="min-w-0 pr-4">
                  <div className="font-semibold text-foreground group-hover:text-brand dark:group-hover:text-brand-dark transition-colors leading-5">
                    {card.title}
                  </div>
                  <div className="text-xs text-muted-foreground">{card.subtitle}</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {card.description}
              </p>
              <ChevronRight className="absolute top-5 right-4 h-4 w-4 text-muted-foreground/40 group-hover:text-brand dark:group-hover:text-brand-dark transition-colors group-hover:translate-x-0.5" />
            </button>
          ))}
        </div>
      </section>

      {/* Why Kaalagyana */}
      <section className="space-y-6">
        <h3 className="text-center text-lg font-semibold text-muted-foreground uppercase tracking-wider">
          Why Kālagñāna
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {whyCards.map((card, idx) => (
            <div
              key={idx}
              className="text-center space-y-3 p-6 rounded-xl border border-border/40 bg-card/50"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 dark:bg-brand-dark/10 text-brand dark:text-brand-dark">
                {card.icon}
              </div>
              <h4 className="font-semibold text-foreground">{card.title}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer tagline */}
      <div className="text-center text-sm text-muted-foreground/60 pt-4">
        Free &middot; No login &middot; No ads
        <nav aria-label="Policies" className="mt-4 flex justify-center gap-4 text-xs text-muted-foreground">
          <a href="/privacy" className="hover:underline">Privacy</a>
          <a href="/terms" className="hover:underline">Terms</a>
          <a href="/support" className="hover:underline">Support</a>
        </nav>
        <p className="mt-2 text-xs text-muted-foreground">&copy; 2025&ndash;2026 K&#x101;lag&#xF1;&#x101;na &middot; Sai Nanduru</p>
      </div>
    </div>
  );
};

export default Landing;
